import type { GpuContext } from '../context.js';
import { workgroupCount } from '../context.js';
import { GpuImage } from '../resources.js';
import { Struct } from '../uniform-writer.js';
import { fillImage } from './fill.js';

/** A polyline in texture space; consecutive points become brush segments. */
export interface StrokePoint {
  x: number;
  y: number;
}

export type PaintTarget = 'erase' | 'restore' | 'reset';

export interface BrushOptions {
  radius: number;
  /** 0 = fully soft, 1 = hard edge. */
  hardness?: number;
  /** Per-application strength. */
  flow?: number;
}

const TARGET_VALUE: Record<PaintTarget, number> = { erase: -1, restore: 1, reset: 0 };

/** Allocates the manual override map, initialised to "untouched" everywhere. */
export function createPaintMap(ctx: GpuContext, width: number, height: number): GpuImage {
  const map = GpuImage.create(ctx.device, width, height, 'r32float', 'paint-map');
  clearPaintMap(ctx, map);
  return map;
}

/** Resets every override back to "let the network decide". */
export function clearPaintMap(ctx: GpuContext, map: GpuImage): void {
  fillImage(ctx, map, [0, 0, 0, 0]);
}

/**
 * Applies a stroke to the override map.
 *
 * Submits before returning: the segment buffer is recycled afterwards, and a
 * stroke is an interactive edit that needs to be on screen immediately anyway.
 */
export function paintStroke(
  ctx: GpuContext,
  map: GpuImage,
  points: readonly StrokePoint[],
  target: PaintTarget,
  brush: BrushOptions,
): void {
  if (points.length === 0) return;

  // A single tap becomes a zero-length segment, which the shader handles as a dot.
  const segments: number[] = [];
  if (points.length === 1) {
    const p = points[0]!;
    segments.push(p.x, p.y, p.x, p.y);
  } else {
    for (let i = 1; i < points.length; i++) {
      const a = points[i - 1]!;
      const b = points[i]!;
      segments.push(a.x, a.y, b.x, b.y);
    }
  }
  const count = segments.length / 4;

  const radius = Math.max(0.5, brush.radius);
  const bounds = strokeBounds(points, radius, map.width, map.height);
  if (bounds.width <= 0 || bounds.height <= 0) return;

  const data = new Float32Array(segments);
  const buffer = ctx.storageBuffer(data.byteLength, 0, 'paint-segments');
  ctx.queue.writeBuffer(buffer, 0, data);

  const params = new Struct(48)
    .i(0, bounds.x, bounds.y)
    .i(8, bounds.width, bounds.height)
    .i(16, map.width, map.height)
    .u(24, count, 0)
    .f(32, radius, brush.hardness ?? 0.6, brush.flow ?? 1, TARGET_VALUE[target]);

  ctx.dispatch(
    {
      module: 'paint.wgsl',
      bindings: [
        { kind: 'storageTexture', format: 'r32float', access: 'read-write' },
        { kind: 'buffer', access: 'read-only' },
        { kind: 'uniform' },
      ],
    },
    [map, buffer, ctx.uniform(params.bytes)],
    [workgroupCount(bounds.width, 8), workgroupCount(bounds.height, 8)],
    `paint:${target}`,
  );

  ctx.flush();
  ctx.releaseBuffer(buffer);
}

function strokeBounds(
  points: readonly StrokePoint[],
  radius: number,
  width: number,
  height: number,
): { x: number; y: number; width: number; height: number } {
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const point of points) {
    minX = Math.min(minX, point.x);
    minY = Math.min(minY, point.y);
    maxX = Math.max(maxX, point.x);
    maxY = Math.max(maxY, point.y);
  }
  const pad = Math.ceil(radius) + 2;
  const x = Math.max(0, Math.floor(minX) - pad);
  const y = Math.max(0, Math.floor(minY) - pad);
  return {
    x,
    y,
    width: Math.min(width, Math.ceil(maxX) + pad) - x,
    height: Math.min(height, Math.ceil(maxY) + pad) - y,
  };
}
